import type { HistoricSalary, JobSalary, Quantile } from "@lossdog/job-watchlists";
import { useCallback, useEffect, useRef, useState } from "react";

import type { HistoricalSalaryDataPoint, TimePeriod } from "@/components/watchlist/charts";
import { useJobSalaryContext } from "@/data/watchlist/JobSalary.context";

interface UseHistoricalSalaryDataOptions {
  jobSalary: JobSalary | null;
  quantile: Quantile | null;
  timePeriod?: TimePeriod;
}

interface UseHistoricalSalaryDataReturn {
  data: HistoricalSalaryDataPoint[];
  minSalary: number;
  maxSalary: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

function quantileToSalary(data: HistoricSalary, quantile: Quantile | null): number {
  if (!quantile) {
    return data.a_median;
  }

  switch (quantile.id) {
    case "a_pct10":
      return data.a_pct10;
    case "a_pct25":
      return data.a_pct25;
    case "a_median":
      return data.a_median;
    case "a_pct75":
      return data.a_pct75;
    case "a_pct90":
      return data.a_pct90;
    default:
      return data.a_median;
  }
}

export function formatDate(date: Date): string {
  return `${date.getMonth() + 1}/${date.getFullYear()}`;
}

export function toHistoricSalaryDataPoint(jobSalary: JobSalary | null): HistoricalSalaryDataPoint {
  const date = new Date();

  return {
    rawDate: date,
    date: formatDate(date),
    baseSalary: jobSalary?.salary.value ?? 0,
  };
}

/**
 * Custom hook to fetch and transform historical salary data for the chart
 */
export const useHistoricalSalaryData = ({
  jobSalary,
  quantile,
  timePeriod = "five-year",
}: UseHistoricalSalaryDataOptions): UseHistoricalSalaryDataReturn => {
  const { jobSalaryClient, isLoading: contextLoading } = useJobSalaryContext();
  const [data, setData] = useState<HistoricalSalaryDataPoint[]>([]);
  const [minSalary, setMinSalary] = useState(0);
  const [maxSalary, setMaxSalary] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the current request to handle cancellation
  const requestIdRef = useRef(0);
  const currentJobSalaryIdRef = useRef<string | null>(null);

  const fetchHistoricalData = useCallback(async () => {
    if (!jobSalaryClient || !jobSalary || contextLoading) {
      setData([]);
      return;
    }

    // Generate a unique request ID for this fetch
    const requestId = ++requestIdRef.current;
    const jobSalaryId = jobSalary.id;
    currentJobSalaryIdRef.current = jobSalaryId;

    setIsLoading(true);
    setError(null);

    try {
      // Currently only 'ytd' is supported by the server
      const historicData = await jobSalaryClient.fetchHistoricSalaryData(jobSalary, timePeriod);

      // Check if this request is still valid (not superseded by a newer request)
      if (requestId !== requestIdRef.current) {
        return;
      }

      // Check if the job salary has changed
      if (currentJobSalaryIdRef.current !== jobSalaryId) {
        return;
      }

      if (!historicData || historicData.length === 0) {
        setData([]);
        return;
      }

      // Transform the data to match HistoricalSalaryDataPoint interface
      let tempMinSalary = Number.MAX_SAFE_INTEGER;
      let tempMaxSalary = Number.MIN_SAFE_INTEGER;
      const transformedData: HistoricalSalaryDataPoint[] = historicData.map((point) => {
        const date = new Date(point.time); // Timestamp is already in milliseconds

        const salary = Math.round(quantileToSalary(point, quantile));

        tempMinSalary = Math.min(tempMinSalary, salary);
        tempMaxSalary = Math.max(tempMaxSalary, salary);

        return {
          rawDate: date,
          date: formatDate(date),
          baseSalary: salary,
        };
      });

      // Sort by date to ensure proper chart rendering
      transformedData.sort((a, b) => {
        return a.rawDate.getTime() - b.rawDate.getTime();
      });

      setMinSalary(tempMinSalary);
      setMaxSalary(tempMaxSalary);
      setData(transformedData);
    } catch (err) {
      // Check if this request is still valid before setting error
      if (requestId !== requestIdRef.current) {
        return;
      }

      if (currentJobSalaryIdRef.current !== jobSalaryId) {
        return;
      }

      const errorMessage = err instanceof Error ? err.message : "Failed to fetch historical salary data";
      setError(errorMessage);
      setData([]);
    } finally {
      // Only set loading to false if this is still the current request
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [jobSalaryClient, jobSalary, contextLoading, quantile, timePeriod]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchHistoricalData();

    // Cleanup function: mark current requests as stale when dependencies change
    return () => {
      // Increment request ID to invalidate any in-flight requests
      // We intentionally mutate the ref here to cancel in-flight requests
      // eslint-disable-next-line react-hooks/exhaustive-deps
      requestIdRef.current++;
    };
  }, [fetchHistoricalData]);

  const refetch = useCallback(() => {
    fetchHistoricalData();
  }, [fetchHistoricalData]);

  return {
    data,
    minSalary,
    maxSalary,
    isLoading: isLoading || contextLoading,
    error,
    refetch,
  };
};
